import { Component, OnInit, Output, EventEmitter, Input } from '@angular/core';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule, AbstractControl } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { Router, ActivatedRoute } from '@angular/router';
import { CoreService } from '../../../../services/core.service';
import { SubmitGridComponent } from '../../components/submit-grid/submit-grid.component';
import { SectionGridComponent } from '../../components/section-grid/section-grid.component';
import { StepperGridComponent } from '../../components/stepper-grid/stepper-grid.component';
import { FooterGridComponent } from '../../components/footer-grid/footer-grid.component';

@Component({
  selector: 'app-create-user',
  standalone: true,
  imports: [ReactiveFormsModule, CommonModule, SectionGridComponent, SubmitGridComponent, StepperGridComponent, FooterGridComponent],
  templateUrl: './create-user.component.html',
  styleUrl: './create-user.component.scss'
})
export class CreateUserComponent {

  @Input() embeddedMode = false;
  @Input() companyIdInput: string | null = null;
  userForm: FormGroup;
  userGroups: any[] = [];
  companies: any[] = [];
  companyId: string | null = null;
  formData: any = { ID: null };
  hidePasswordField = true;
  hideConfirmPasswordField = true;
  isLoadingCompanies = false;
  @Input() hasBorder: boolean = true;

  @Output() onSubmitClbk = new EventEmitter<any>();

  constructor(
    private fb: FormBuilder,
    private coreService: CoreService,
    private router: Router,
    private route: ActivatedRoute
  ) {
    this.userForm = this.fb.group({
      Email: ['', [Validators.required, Validators.email]],
      Password: ['', [Validators.required, Validators.minLength(6)]],
      ConfirmPassword: ['', Validators.required],
      FirstName: ['', Validators.required],
      LastName: ['', Validators.required],
      Phone: ['', [Validators.required, Validators.pattern(/^\d{10}$/)]],
      UserGroupID: ['', Validators.required],
      companyID: [{ value: '' }, Validators.required]
    }, { validators: this.passwordMatchValidator });
  }

  ngOnInit() {
    // Get company ID from route parameter if available
    this.route.params.subscribe(params => {
      this.companyId = this.companyIdInput || params['company'];
      if (this.companyId) {
        this.userForm.patchValue({
          companyID: this.companyId
        });
      }
    });

    this.loadUserGroups();
    this.loadCompanies();
  }

  // Custom validator for password match
  passwordMatchValidator(control: AbstractControl) {
    const password = control.get('Password')?.value;
    const confirmPassword = control.get('ConfirmPassword')?.value;

    if (password !== confirmPassword) {
      control.get('ConfirmPassword')?.setErrors({ passwordMismatch: true });
      return { passwordMismatch: true };
    }
    return null;
  }

  getControl(controlName: string) {
    return this.userForm.get(controlName);
  }

  hidePassword(): boolean {
    return this.hidePasswordField;
  }

  hideConfirmPassword(): boolean {
    return this.hideConfirmPasswordField;
  }

  toggleVisibility(field: string, event: Event) {
    event.preventDefault();
    if (field === 'password') {
      this.hidePasswordField = !this.hidePasswordField;
    } else if (field === 'confirmPassword') {
      this.hideConfirmPasswordField = !this.hideConfirmPasswordField;
    }
  }

  onSubmit(): void {
    if (this.userForm.valid) {
      const formData = this.userForm.getRawValue();

      // Remove confirm password from submission data
      const { ConfirmPassword, ...userData } = formData;

      this.coreService.postData('manageAccount/user/store', formData).subscribe({
        next: (data) => {
          if (this.embeddedMode) {
            this.onSubmitClbk.emit(data);
          } else {
            // const companyId = data.companyID || data.data?.companyID;
            const companyId = data.companyID || data.companyID || data.data?.companyID || data.data?.companyID;
            if (companyId) {
              this.router.navigate(['/manage-account/create-site', companyId]);
            } else {
              console.error('No company ID found in response:', data);
            }

            // Optional: Reset form
            // this.resetForm();
          }
        },
        error: (error) => {
          console.error('Error creating/updating user:', error);
        }
      });
    } else {
      this.markFormGroupTouched(this.userForm);
    }
  }

  private loadUserGroups(): void {
    this.coreService.getData('/manageAccount/user-groups').subscribe({
      next: (data) => {
        this.userGroups = data || [];
      },
      error: (error) => {
        console.error('Error loading user groups:', error);
      }
    });
  }

  private loadCompanies(): void {
    this.isLoadingCompanies = true;

    this.coreService.getData('/manageAccount/company').subscribe({
      next: (data) => {
        this.companies = data || [];
        this.isLoadingCompanies = false;

        // After companies are loaded, set the company ID from params
        if (this.companyId) {
          // Check if the company exists in the list
          const companyExists = this.companies.some(company =>
            company.ID == this.companyId || company.id == this.companyId
          );

          if (companyExists) {
            // Set the form value
            this.userForm.patchValue({
              companyID: this.companyId
            });

            // Keep it disabled
            this.userForm.get('companyID')?.disable();
          } else {
            console.warn(`Company with ID ${this.companyId} not found in companies list`);
          }
        }
      },
      error: (error) => {
        console.error('Error loading companies:', error);
        this.isLoadingCompanies = false;
      }
    });
  }

  private loadUserData(userId: string): void {
    this.coreService.getData(`manageAccount/user/${userId}`).subscribe({
      next: (data) => {
        this.userForm.patchValue(data);
      },
      error: (error) => {
        console.error('Error loading user data:', error);
      }
    });
  }

  private markFormGroupTouched(formGroup: FormGroup) {
    Object.values(formGroup.controls).forEach(control => {
      control.markAsTouched();

      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      }
    });
  }

  resetForm(): void {
    this.userForm.reset({
      Email: '',
      Password: '',
      ConfirmPassword: '',
      FirstName: '',
      LastName: '',
      Phone: '',
      UserGroupID: '',
      companyID: this.companyId || ''
    });
    this.userForm.markAsPristine();
    this.userForm.markAsUntouched();
  }
}
